'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Search, Bell, User, Menu, X, ChevronDown, BookOpen, LogIn, Shield, Mail, MapPin } from 'lucide-react';
import Image from 'next/image';

const navItems = [
  { label: 'Find Schools', href: '/school-search-discovery', icon: Search },
  { label: 'Schools by Area', href: '/schools-near', icon: MapPin },
  { label: 'School Comparison', href: '/school-profile-comparison', icon: BookOpen },
  { label: 'Contact', href: '/contact', icon: Mail },
];

export default function Topbar() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  return (
    <header className="w-full bg-white border-b border-border shadow-sm">
      {/* Logo banner — DESKTOP/TABLET only (sm and above): logo + tagline + five USP features */}
      <div className="hidden sm:block w-full bg-white px-4 pt-4 pb-3">
        <div className="max-w-screen-2xl mx-auto">
          {/* Logo + tagline row */}
          <Link href="/" className="flex flex-col items-center mb-3">
            <Image
              src="/assets/images/ChatGPT_Image_Jul_24__2026_at_10_20_04_AM-1784874067804.png"
              alt="SchoolMapper — Simplify your school search"
              width={720}
              height={207}
              className="h-auto object-contain"
              style={{ width: 'clamp(480px, 55vw, 720px)' }}
              priority
            />
          </Link>
        </div>
      </div>
      {/* MOBILE top utility row — compact nav controls only; branded hero lives in page content */}
      <div className="sm:hidden flex w-full items-center justify-between bg-white px-3 py-1.5 border-b border-border/50" style={{ boxSizing: 'border-box', maxWidth: '100%', overflow: 'hidden' }}>
        {/* SchoolMapper wordmark — tappable home link */}
        <Link href="/" className="flex items-center gap-1.5 min-w-0">
          <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="#1aaa6e" aria-hidden="true"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
          <span className="text-sm font-bold tracking-tight truncate" style={{ color: '#1e2d5a' }}>SchoolMapper</span>
        </Link>
        {/* Mobile right actions */}
        <div className="flex items-center gap-1 shrink-0">
          <button className="relative p-2 rounded-lg text-muted-foreground hover:bg-muted transition-all duration-150">
            <Bell size={18} />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
          </button>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="p-2 rounded-lg border border-border hover:bg-muted transition-all duration-150"
          >
            <User size={18} className="text-primary" />
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 rounded-lg text-muted-foreground hover:bg-muted transition-all duration-150"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {/* Nav bar below logo — DESKTOP/TABLET only */}
      <div className="hidden sm:block border-t border-border bg-white/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between h-12">
            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems?.map((item) => {
                const active = pathname === item?.href;
                return (
                  <Link
                    key={`nav-${item?.href}`}
                    href={item?.href}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                      active
                        ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <item.icon size={15} />
                    {item?.label}
                  </Link>
                );
              })}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-1 sm:gap-2 ml-auto">
              {/* Notification bell */}
              <button className="relative p-2.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150">
                <Bell size={18} />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
              </button>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-1.5 pl-2 pr-2 sm:pr-3 py-1.5 rounded-lg border border-border hover:bg-muted transition-all duration-150"
                >
                  <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center">
                    <User size={14} className="text-primary" />
                  </div>
                  <span className="text-sm font-medium text-foreground hidden sm:block">Account</span>
                  <ChevronDown size={14} className="text-muted-foreground hidden sm:block" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-border rounded-xl shadow-lg py-1.5 z-50 animate-fade-in">
                    <Link
                      href="/sign-up-login"
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <LogIn size={15} className="text-muted-foreground" />
                      Sign In / Register
                    </Link>
                    <div className="border-t border-border my-1" />
                    <div className="px-4 py-2">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Demo Roles</p>
                    </div>
                    <Link
                      href="/school-admin-portal"
                      className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <BookOpen size={15} className="text-amber-500" />
                      School Admin Portal
                    </Link>
                    <Link
                      href="/admin-backend"
                      className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Shield size={15} className="text-red-500" />
                      Admin Backend
                    </Link>
                  </div>
                )}
              </div>

              {/* Mobile Hamburger — shown on tablet (md and below) */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden p-2.5 rounded-lg text-muted-foreground hover:bg-muted transition-all duration-150"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Mobile user menu dropdown */}
      {userMenuOpen && (
        <div className="sm:hidden absolute right-3 top-14 w-52 bg-white border border-border rounded-xl shadow-lg py-1.5 z-50 animate-fade-in">
          <Link
            href="/sign-up-login"
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
            onClick={() => setUserMenuOpen(false)}
          >
            <LogIn size={15} className="text-muted-foreground" />
            Sign In / Register
          </Link>
          <div className="border-t border-border my-1" />
          <Link
            href="/school-admin-portal"
            className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
            onClick={() => setUserMenuOpen(false)}
          >
            <BookOpen size={15} className="text-amber-500" />
            School Admin Portal
          </Link>
          <Link
            href="/admin-backend"
            className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
            onClick={() => setUserMenuOpen(false)}
          >
            <Shield size={15} className="text-red-500" />
            Admin Backend
          </Link>
        </div>
      )}
      {/* Mobile Nav Drawer */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-white animate-fade-in shadow-md">
          <div className="px-4 py-3 space-y-1">
            {navItems?.map((item) => {
              const active = pathname === item?.href;
              return (
                <Link
                  key={`mobile-nav-${item?.href}`}
                  href={item?.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                    active
                      ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <item.icon size={18} />
                  {item?.label}
                </Link>
              );
            })}
            <div className="pt-2 border-t border-border">
              <Link
                href="/sign-up-login"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium text-primary hover:bg-primary/5 transition-all duration-150"
              >
                <LogIn size={18} />
                Sign In / Register
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}